import { gql, useMutation } from '@apollo/client'
import omitBy from 'lodash/omitBy'

import {
  SendVerificationEmail,
  SendVerificationEmailMutation,
  SendVerificationEmailMutationVariables,
  SignupAsArtist,
  SignupAsArtistInput,
  SignupAsArtistMutation,
  SignupAsArtistMutationVariables,
  VerifyCode,
  VerifyCodeMutation,
  VerifyCodeMutationVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useAuth } from '@/core/auth'
import {
  getInputSignUpArtist,
  getRedirectAfterLoginPath,
  setRedirectAfterLoginPath,
} from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showMessage } = useMessage()
  const { t } = useTranslation('mailAuthArtist')
  const { loginWithEmail, getUserClaims } = useAuth()

  const [verifyCode] = useMutation<
    VerifyCodeMutation,
    VerifyCodeMutationVariables
  >(VerifyCode)

  const [sendVerifyEmail, { loading: verifyEmailSending }] = useMutation<
    SendVerificationEmailMutation,
    SendVerificationEmailMutationVariables
  >(SendVerificationEmail, {
    onCompleted() {
      showMessage(t('resendCodeSuccess'), 'success')
    },
  })

  const [signupAsArtist] = useMutation<
    SignupAsArtistMutation,
    SignupAsArtistMutationVariables
  >(SignupAsArtist)

  return {
    state: {
      loading: verifyEmailSending,
    },
    actions: {
      async onSubmit({ code }) {
        const { value: loginPath } = await getRedirectAfterLoginPath()
        const singUpInfo = await getInputSignUpArtist()
        const { data } = await verifyCode({
          variables: { code, email: singUpInfo.email },
        })

        if (!data?.verifyCode.verified) throw new Error(t('verifyFailed'))

        const signUpAsArtistInputs = omitBy(
          singUpInfo,
          v => !v
        ) as SignupAsArtistInput

        await signupAsArtist({
          variables: {
            inputs: signUpAsArtistInputs,
          },
        })

        await loginWithEmail(singUpInfo.email, singUpInfo.password)
        // get user claims data
        await getUserClaims()
        await setRedirectAfterLoginPath(loginPath ?? '')
      },
      async onResendAuthMail() {
        const { email } = await getInputSignUpArtist()
        await sendVerifyEmail({ variables: { email } })
      },
    },
  }
})

const SIGN_UP_AS_ARTIST = gql`
  mutation SignupAsArtist($inputs: SignupAsArtistInput!) {
    signupAsArtist(inputs: $inputs) {
      id
      accountId
      formattedAccountId @client
      avatar {
        uri
        smallUri
      }
      displayName
    }
  }
`
